import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { kbEntryForm, kbEntryType, kbSeverity, type KbEntryForm } from '@repo-manager/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@repo-manager/ui';

import { getApi } from '../lib/api';
import { getSupabase } from '../lib/supabase';

export async function newKbAction(): Promise<null> {
  return null;
}

export function NewKbPage() {
  const navigate = useNavigate();
  const form = useForm<KbEntryForm>({
    resolver: zodResolver(kbEntryForm),
    defaultValues: {
      title: '',
      slug: '',
      type: 'pattern',
      severity: 'recommended',
      summary: '',
      content: '',
      rationale: '',
      code_example: '',
      stack_layers: [],
      technologies: [],
      workspaces: [],
      tags: [],
    },
  });

  async function onSubmit(values: KbEntryForm) {
    const supabase = getSupabase();
    const { data: inserted, error } = await supabase
      .from('kb_entries')
      .insert({
        title: values.title,
        slug: values.slug,
        type: values.type,
        severity: values.severity,
        summary: values.summary,
        content: values.content,
        rationale: values.rationale || null,
        code_example: values.code_example || null,
        stack_layers: values.stack_layers,
        technologies: values.technologies,
        workspaces: values.workspaces,
        status: 'draft',
      })
      .select('id, slug')
      .single();
    if (error || !inserted) {
      toast.error('Failed to create entry', { description: error?.message });
      return;
    }
    if (values.tags.length > 0) {
      await supabase
        .from('kb_tags')
        .insert(values.tags.map((tag) => ({ entry_id: inserted.id, tag })));
    }
    try {
      await getApi().kbEmbed([inserted.slug]);
      toast.success('Entry created + embedded');
    } catch (e) {
      toast.warning('Entry created; embedding will retry', {
        description: e instanceof Error ? e.message : String(e),
      });
    }
    navigate(`/knowledge/${inserted.slug}`);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">New KB entry</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Draft</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Slug</FormLabel>
                      <FormControl><Input placeholder="rls-required-on-tables" {...field} /></FormControl>
                      <FormDescription>Lowercase, hyphens. Used as the URL + cross-refs.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {kbEntryType.options.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="severity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Severity</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {kbSeverity.options.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Summary</FormLabel>
                    <FormControl><Textarea rows={2} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content</FormLabel>
                    <FormControl><Textarea rows={10} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rationale"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rationale (optional)</FormLabel>
                    <FormControl><Textarea rows={4} {...field} value={field.value ?? ''} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code_example"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code example (optional)</FormLabel>
                    <FormControl><Textarea rows={8} className="font-mono text-xs" {...field} value={field.value ?? ''} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2">
                <Button type="submit" disabled={form.formState.isSubmitting}>Create draft</Button>
                <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
